"use client";

/**
 * GlassEngineV3 — forked from app/v2/GlassEngine.tsx (app/v2/ is never
 * modified) for the /v3 pages. Same "chain of custody" machine (source →
 * compute → inspect → HUMAN GATE → deliver), same class names, so it reuses
 * the `.v3reuse .engine …` styling already in REUSE_CSS (../shared.tsx) — no
 * separate stylesheet, exactly like ShowcaseRowV3.
 *
 * Two Round-5 changes vs. the v2 original:
 *
 *  1. STACK LANGUAGE (Jay): the lead paragraph dropped the infra-vendor
 *     jargon ("AWS Step Functions + GPU-per-instance"). The scale beat is now
 *     capability copy ("One review or a thousand — same engine, same
 *     evening.") and the stack sentence is deliberately precise about what is
 *     and isn't self-hosted: rendering runs on our own GPU fleet; language
 *     runs on enterprise AI services (zero data retention) — NOT open-source,
 *     NOT self-hosted; and the numbers come from no model at all.
 *
 *  2. CYCLIC ANIMATION (Jay): the v2 engine ran once and HELD on the
 *     delivered end-state. This one loops forever: a client's dossier travels
 *     the chain, is signed, delivers, then exits to the right; the next
 *     client's dossier (names rotate through Rachel → John → Priya → Marcus →
 *     Elena) enters from the left and runs; repeat. Under
 *     prefers-reduced-motion it renders a single static end-state, no motion.
 *
 * Uses direct DOM refs + timeouts (mirroring the v2 timeline) rather than
 * re-deriving the whole thing as React state — this is a scripted animation
 * loop, not app state.
 */

import { useEffect, useRef } from "react";

// Dwell offsets (ms) from the start of each cycle. HALT is deliberately the
// longest — the human gate is the climax, not a rest stop.
const T_COMPUTE = 2000;
const T_INSPECT = 5200;
const T_HALT_START = 7600;
const HALT_MS = 5600;
const T_APPROVE = T_HALT_START + HALT_MS; // 13200
const T_DELIVER = T_APPROVE + 1200; // 14400 — brief "approved" hold before fan-out
const T_EXIT = T_DELIVER + 2200; // 16600 — hold on delivered, then slide out
const T_NEXT = T_EXIT + 1200; // 17800 — after slide-out, reset + next client

// Clients cycle through the machine, one per loop.
const CLIENTS = ["Rachel", "John", "Priya", "Marcus", "Elena"];
const runIdFor = (name: string) => `${name.toLowerCase()}-q2`;

export function GlassEngineV3() {
  const rootRef = useRef<HTMLDivElement>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)")
      .matches;
    const $ = <T extends HTMLElement>(id: string) =>
      root.querySelector<T>(`#${id}`);

    const stationIds = ["st1", "st2", "st3", "st4", "st5"];
    const stations = stationIds
      .map((id) => $(id))
      .filter((el): el is HTMLElement => el !== null);
    const dossier = $("dossier");
    const dosname = $("dosname");
    const state = $("doststate");
    const phase = $("phaselbl");
    const gate = $("gate");
    const gatestamp = $("gatestamp");
    const rid = $("runid");
    const belt = root.querySelector<HTMLElement>(".belt");
    const POS = ["10%", "30%", "50%", "70%", "90%"];

    if (!dossier || !dosname || !state || !phase || !gate || !gatestamp || !belt)
      return;

    const cancelled = { current: false };
    let timeouts: ReturnType<typeof setTimeout>[] = [];
    let tallyTimer: ReturnType<typeof setInterval> | undefined;
    const after = (ms: number, fn: () => void) => {
      timeouts.push(
        setTimeout(() => {
          if (!cancelled.current) fn();
        }, ms)
      );
    };

    const setStation = (i: number) => {
      stations.forEach((s, j) => s.classList.toggle("live", j === i));
      dossier.style.left = POS[i];
    };
    const setPhase = (txt: string, cls?: string) => {
      phase.textContent = txt;
      phase.className = "ph" + (cls ? " " + cls : "");
    };

    if (reduce) {
      // Static everything-on end-state — no motion at all.
      stations.forEach((s) => s.classList.add("live"));
      ["c1", "c2", "c3"].forEach((id) => $(id)?.classList.add("show"));
      const tally = $("tally");
      if (tally) tally.textContent = "+15.7%";
      ["i1", "i2", "i3"].forEach((id) => $(id)?.classList.add("pass"));
      gate.classList.add("open");
      gate.textContent = "✓";
      gatestamp.classList.add("show");
      // f4 (the private feed) is deliberately never lit — not built yet.
      ["f1", "f2", "f3"].forEach((id) => $(id)?.classList.add("lit"));
      $("xbook")?.classList.add("lit");
      dossier.style.left = "90%";
      setPhase("05 · delivered — three doors", "go");
      return;
    }

    const resetVisuals = () => {
      stations.forEach((s) => s.classList.remove("live"));
      ["c1", "c2", "c3"].forEach((id) => $(id)?.classList.remove("show"));
      ["i1", "i2", "i3"].forEach((id) => $(id)?.classList.remove("pass"));
      ["f1", "f2", "f3"].forEach((id) => $(id)?.classList.remove("lit"));
      $("xbook")?.classList.remove("lit");
      const tally = $("tally");
      if (tally) tally.textContent = "0.0%";
      gate.classList.remove("open", "wait");
      gate.textContent = "■";
      gatestamp.classList.remove("show");
      dossier.classList.remove("held", "cleared", "exiting");
    };

    function startCycle(idx: number) {
      if (cancelled.current) return;
      timeouts = []; // previous cycle's timeouts have all fired by now
      const name = CLIENTS[idx % CLIENTS.length];
      const runId = runIdFor(name);
      if (rid) {
        rid.textContent = `run · ${runId}-2026 — same evening as the rest of the book`;
      }
      dosname!.textContent = runId;
      state!.textContent = "· sourcing";

      // Enter from the left: snap off-screen with no transition, then slide in.
      dossier!.classList.add("entering");
      dossier!.style.left = "-20%";
      // Force reflow so the "no transition at -20%" commits before we animate.
      void dossier!.offsetWidth;

      after(50, () => {
        dossier!.classList.remove("entering");
        // 01 · source
        setStation(0);
        setPhase("01 · sourcing — cited feeds streaming");
      });

      // 02 · compute
      after(T_COMPUTE, () => {
        setStation(1);
        state!.textContent = "· computing";
        setPhase("02 · computing — code does the math");
        $("c1")?.classList.add("show");
        const vals = ["+3.2%", "+7.8%", "+11.4%", "+14.1%", "+15.3%", "+15.7%"];
        let i = 0;
        tallyTimer = setInterval(() => {
          if (cancelled.current) {
            clearInterval(tallyTimer);
            return;
          }
          const tally = $("tally");
          if (tally) tally.textContent = vals[i++];
          if (i === vals.length) {
            clearInterval(tallyTimer);
            $("c2")?.classList.add("show");
            after(300, () => $("c3")?.classList.add("show"));
          }
        }, 220);
      });

      // 03 · inspect (agent gauntlet)
      after(T_INSPECT, () => {
        setStation(2);
        state!.textContent = "· inspection";
        setPhase("03 · inspection — facts-only check");
        after(400, () => $("i1")?.classList.add("pass"));
        after(950, () => $("i2")?.classList.add("pass"));
        after(1500, () => $("i3")?.classList.add("pass"));
      });

      // 04 · THE HUMAN GATE — the climax. Belt halts, ghosts freeze.
      after(T_HALT_START, () => {
        setStation(3);
        state!.textContent = "· HELD";
        dossier!.classList.add("held");
        belt!.classList.add("halted");
        gate!.classList.add("wait");
        gate!.textContent = "■";
        setPhase("04 · HALTED — awaiting advisor sign-off", "halt");
      });

      // Advisor signs off
      after(T_APPROVE, () => {
        belt!.classList.remove("halted");
        dossier!.classList.remove("held");
        dossier!.classList.add("cleared");
        state!.textContent = "· approved";
        gate!.classList.remove("wait");
        gate!.classList.add("open");
        gate!.textContent = "✓";
        gatestamp!.classList.add("show");
        setPhase("04 · approved — advisor sign-off · logged", "go");
      });

      // 05 · fan-out to every door
      after(T_DELIVER, () => {
        setStation(4);
        state!.textContent = "· delivering";
        setPhase("05 · delivered — three doors", "go");
        // f4 (the private feed) deliberately never lights up — not built yet.
        ["f1", "f2", "f3"].forEach((id, i) =>
          after(300 + i * 260, () => $(id)?.classList.add("lit"))
        );
        after(1400, () => $("xbook")?.classList.add("lit"));
      });

      // Exit right, then reset and run the next client.
      after(T_EXIT, () => {
        state!.textContent = "· delivered";
        dossier!.classList.add("exiting");
        dossier!.style.left = "120%";
      });
      after(T_NEXT, () => {
        resetVisuals();
        startCycle(idx + 1);
      });
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            startCycle(0);
            io.disconnect();
          }
        });
      },
      { threshold: 0.25 }
    );
    io.observe(root);

    return () => {
      cancelled.current = true;
      io.disconnect();
      timeouts.forEach(clearTimeout);
      if (tallyTimer) clearInterval(tallyTimer);
    };
  }, []);

  return (
    <div className="engine" id="engine" ref={rootRef}>
      <div className="lbl">The glass engine</div>
      <h2>Watch one review travel the chain of custody.</h2>
      {/* Round 7 — split at the register change. This was one 76-word block
          carrying five claims, which read as a wall of grey. The first three
          describe the thing you are about to watch, so they stay here. The
          infrastructure claims answer a different question, asked by a
          different person (a CTO or a CCO, not an advisor), so they moved
          below the diagram as <p className="subfoot">. No wording changed. */}
      <p className="sub">
        Every door above opens onto this machine. Numbers are computed,
        checked against a facts-only rule, and held at a human gate — nothing
        reaches a client until the advisor stamps it, and nothing needing
        judgment gets answered without you. One review or a thousand — same
        engine, same evening.
      </p>
      <div className="modes">
        <span className="mode on">
          <b>Scheduled</b> — the whole book, every quarter (what&rsquo;s
          running below)
        </span>
        <span className="mode">
          <b>On-demand</b> — a client asks; the same engine computes a fresh
          answer in seconds
        </span>
      </div>

      <div className="machine">
        <div className="runline">
          <span className="rid" id="runid">
            run · rachel-q2-2026 — same evening as the rest of the book
          </span>
          <span className="ph" id="phaselbl">
            01 · sourcing — cited feeds streaming
          </span>
        </div>
        <div className="belt">
          <div className="beltline" />
          <div className="dossier ghost g1">tom-q2</div>
          <div className="dossier ghost g2">meera-q2</div>
          <div className="dossier ghost g3">walt-q2</div>
          <div className="dossier ghost g4">june-q2</div>
          <div className="dossier ghost od">live question</div>
          <div className="dossier" id="dossier">
            <span id="dosname">rachel-q2</span>{" "}
            <span className="st" id="doststate">
              · sourcing
            </span>
          </div>
        </div>
        <div className="stations">
          <div className="station" id="st1">
            <div className="art">
              <div className="tickroll">
                FEED · VTI 271.44 ▲<br />
                FEED · AAPL 243.10 ▼<br />
                FRED · 10Y 4.21%<br />
                FEED · VNQ 91.02 ▲<br />
                FEED · BX 148.77 ▼<br />
                FRED · CPI 2.6%
              </div>
            </div>
            <div className="num">01 · SOURCE</div>
            <h3>Data flows in</h3>
            <p>
              Cited feeds only — <b>licensed market data</b>, <b>Federal
              Reserve</b> economics. Nothing enters uncited.
            </p>
          </div>
          <div className="station" id="st2">
            <div className="art">
              <div className="comp">
                <div className="line" id="c1">
                  return_q2 = <span className="big" id="tally">
                    0.0%
                  </span>
                </div>
                <div className="line" id="c2">
                  <span className="src">
                    Δ 2026-04-01 → 06-30 · receipt attached
                  </span>
                </div>
                <div className="line" id="c3">
                  corr_spy = <b>0.71</b>{" "}
                  <span className="src">· 252-day · computed</span>
                </div>
              </div>
            </div>
            <div className="num">02 · COMPUTE</div>
            <h3>The math is done by code</h3>
            <p>
              Watch it tally — every figure{" "}
              <b>calculated, never written by AI</b>, receipt attached.
            </p>
          </div>
          <div className="station" id="st3">
            <div className="art">
              <div className="gauntlet">
                <div className="insp" id="i1">
                  <span className="dot">✓</span> Computed, not written
                </div>
                <div className="insp" id="i2">
                  <span className="dot">✓</span> Facts only — no advice, no
                  opinion
                </div>
                <div className="insp" id="i3">
                  <span className="dot">✓</span> Judgment? Escalate, don&rsquo;t
                  guess
                </div>
              </div>
            </div>
            <div className="num">03 · INSPECT</div>
            <h3>The facts-only gauntlet</h3>
            <p>
              Every answer is checked against one rule:{" "}
              <b>state what&rsquo;s computed, explain around it</b> — and the
              instant a question needs judgment, flag it for the advisor
              instead of improvising.
            </p>
          </div>
          <div className="station" id="st4">
            <div className="art gateart">
              <div className="gatecirc" id="gate">
                ■
              </div>
              <div className="gatestamp" id="gatestamp">
                approved · advisor sign-off · 12:02 EDT · logged
              </div>
            </div>
            <div className="num">04 · THE HUMAN GATE</div>
            <h3>The belt stops for you</h3>
            <p>
              <b>The machine cannot proceed without the advisor.</b> Who
              approved, when, what changed — books-and-records ready.
            </p>
          </div>
          <div className="station" id="st5">
            <div className="art">
              <div className="fan">
                <span id="f1">video</span>
                <span id="f2">chat</span>
                <span id="f3">phone</span>
                <span id="f4" className="fancoming">
                  feed · coming
                </span>
              </div>
              <div className="xbook" id="xbook">
                × every client, same evening
              </div>
            </div>
            <div className="num">05 · EVERY DOOR</div>
            <h3>One run, three doors today</h3>
            <p>
              The same signed payload becomes the review, the answers, and
              the line. <b>The doors you just hovered.</b> The private feed
              is next on the roadmap, not lit here yet.
            </p>
          </div>
        </div>
      </div>

      <div className="engineclose">
        <p className="line">
          The AI never does the math. You sign everything. That&rsquo;s the
          machine.
        </p>
        {/* Moved down from the intro paragraph (Round 7). It lands after the
            punchline and before the disclaimer, so the order reads: watch the
            machine, hear the claim, see how it is actually run. */}
        <p className="subfoot">
          Self-hosted rendering on our own GPU fleet; enterprise AI services
          with zero data retention for language — and the numbers never come
          from any model at all.
        </p>
        <p className="fine">
          Simulated data · not investment advice · compliance brief for your
          CCO available on request
        </p>
      </div>
    </div>
  );
}
